import type React from "react"
import { useEffect, useMemo, useState } from "react"
import { useLocation } from "react-router-dom"
import styled from "styled-components"
import { ArbeidsarkLogo } from "~/assets/ArbeidsarkLogo"
import { LanguageSelect } from "~/components/Select/LanguageSelect"
import { useTranslate } from "~/ur-react-hooks"
import { useStore } from "~/store"
import { useMedia } from "~/utils/hooks/useMedia"
import { Icon } from "../Icon"
import { HeaderSearch } from "./HeaderSearch"

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 55px;
  background: ${(props) => props.theme.colors.defaultBackground};
  border-bottom: 1px solid ${(props) => props.theme.colors.gray95};
`

const LogoWrapper = styled.div`
  padding: 18px 34px;

  :hover {
    cursor: pointer;
  }
`

const ToggleSidebarButton = styled.div`
  display: none;
  ${(props) => props.theme.media.pad} {
    display: flex;
    color: ${(props) => props.theme.colors.primary};
    padding: 16px 24px;
  }
`

const Title = styled.div`
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 21px;
  letter-spacing: 0em;
  text-align: left;
  padding-left: 97px;
  padding-right: 64px;
`

const MenuWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;

  height: 100%;

  ${(props) => props.theme.media.desktop} {
    justify-content: space-between;
  }

  flex: 1;
  margin-left: auto;

  .language-select {
    margin-left: auto;
  }

  SVG {
    fill: ${(props) => props.theme.colors.primary40};
  }
`

type HeaderProps = {}

export const Header: React.FC<HeaderProps> = () => {
  const sidebarOpen = useStore((state) => state.sidebar.open)
  const setSidebarOpen = useStore((state) => state.setSidebarOpen)
  const searchWorksheets = useStore((state) => state.header.searchWorksheets)
  const setSearchWorksheets = useStore((state) => state.setSearchWorksheets)
  const { mobile, pad, desktop } = useMedia()
  const [collapseSearch, setCollapseSearch] = useState(!desktop)

  const translations = useTranslate({
    searchPleaceholder: "worksheets.search-placeholder",
    users: "common.users",
    myWorksheets: "common.my-worksheets",
    settings: "common.settings",
    home: "common.home",
    subscriptions: "common.subscriptions",
    worksheetGenerator: "worksheets.worksheet-generator",
  })

  const location = useLocation()
  const { title, showSearchbar } = useMemo(() => {
    const { pathname } = location

    let title = "Arbeidsark"
    let showSearchbar = false
    if (/users/.test(pathname)) title = translations.users
    else if (/my-worksheets/.test(pathname)) {
      title = translations.myWorksheets
      showSearchbar = true
    } else if (
      /settings/.test(pathname) ||
      /change-password/.test(pathname) ||
      /invoicing/.test(pathname)
    )
      title = translations.settings
    else if (/subscriptions/.test(pathname)) title = translations.subscriptions
    else if (/dashboard/.test(pathname)) title = translations.home
    else if (/worksheet-generator/.test(pathname))
      title = translations.worksheetGenerator
    return {
      title,
      showSearchbar,
    }
  }, [
    location,
    translations.home,
    translations.myWorksheets,
    translations.settings,
    translations.subscriptions,
    translations.users,
    translations.worksheetGenerator,
  ])
  const toggleSearch = () => setCollapseSearch(!collapseSearch)

  // Make sure search is usable if the app changes from phone/pad to desktop.
  useEffect(() => {
    if (desktop) {
      setCollapseSearch(false)
    } else if (!desktop && searchWorksheets === "") {
      setCollapseSearch(true)
    }
  }, [mobile, pad, desktop, searchWorksheets])

  return (
    <Wrapper>
      {(!mobile || (mobile && collapseSearch)) && (
        <LogoWrapper>
          <ArbeidsarkLogo />
        </LogoWrapper>
      )}
      {desktop && <Title>{title}</Title>}

      <MenuWrapper>
        {!mobile && showSearchbar && (
          <HeaderSearch
            value={searchWorksheets}
            collapse={collapseSearch}
            collapsable={!desktop}
            onToggle={toggleSearch}
            onChange={setSearchWorksheets}
          />
        )}
        {desktop && (
          <LanguageSelect className="language-select" fontWeight={500} />
        )}
        {!sidebarOpen && (
          <ToggleSidebarButton onClick={() => setSidebarOpen(true)}>
            <Icon icon="bars" size="24px" />
          </ToggleSidebarButton>
        )}
      </MenuWrapper>
    </Wrapper>
  )
}
